<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useNotification } from '@kyvg/vue3-notification'
import SubscriptionService from '@/services/SubscriptionService'
import FormInput from '../Atoms/FormInput.vue'
import FormSelect from '../Atoms/FormSelect.vue'

const DEFAULT_COUNTRY = 'US'

const props = defineProps({
  showErrors: {
    type: Boolean,
    default: false,
  },
})

const address = defineModel({
  type: Object,
  default: () => ({ street: '', city: '', state: '', zip: '', country: '' }),
})

const { notify } = useNotification()

const countries = ref([])
const states = ref([])
const cities = ref([])
const loadingCountries = ref(true)
const loadingStates = ref(false)
const loadingCities = ref(false)

const setField = (field, value) => {
  address.value = { ...address.value, [field]: value }
}

const fieldModel = (field) => computed({
  get: () => address.value?.[field] ?? '',
  set: (value) => setField(field, value),
})

const street = fieldModel('street')
const city = fieldModel('city')
const zip = fieldModel('zip')

const country = computed({
  get: () => address.value?.country ?? '',
  set: (value) => {
    address.value = { ...address.value, country: value, state: '', city: '' }
  },
})

const state = computed({
  get: () => address.value?.state ?? '',
  set: (value) => {
    address.value = { ...address.value, state: value, city: '' }
  },
})

const hasStates = computed(() => states.value.length > 0)
// Cities are listed per state; free text only when the dataset has nothing to offer.
const showCitySelect = computed(() =>
  hasStates.value && (!state.value || loadingCities.value || cities.value.length > 0)
)

const countryOptions = computed(() => [
  { value: '', text: 'Country' },
  ...countries.value.map(c => ({ value: c.code, text: c.name })),
])

const stateOptions = computed(() => [
  { value: '', text: 'State' },
  ...states.value.map(s => ({ value: s.code, text: s.name })),
])

const cityOptions = computed(() => {
  const names = cities.value.slice()
  // Keep a previously saved city selectable even if the dataset no longer lists it.
  if (city.value && !names.includes(city.value)) names.unshift(city.value)
  return [{ value: '', text: 'City' }, ...names.map(name => ({ value: name, text: name }))]
})

const missing = computed(() => ({
  country: !country.value,
  street: !street.value.trim(),
  city: !city.value.trim(),
  zip: !zip.value.trim(),
  state: hasStates.value && !state.value,
}))

const isComplete = computed(() =>
  !loadingCountries.value && !loadingStates.value && !loadingCities.value && !Object.values(missing.value).some(Boolean)
)

defineExpose({ isComplete })

let statesRequest = 0
const loadStates = async (countryCode) => {
  const request = ++statesRequest
  states.value = []
  cities.value = []
  if (!countryCode) return
  loadingStates.value = true
  try {
    const result = await SubscriptionService.getBillingStates(countryCode)
    if (request === statesRequest) states.value = result || []
  } catch (e) {
    if (request === statesRequest) notify({ group: 'error', text: 'Unable to load states. Please try again.' })
  } finally {
    if (request === statesRequest) loadingStates.value = false
  }
}

let citiesRequest = 0
const loadCities = async (countryCode, stateCode) => {
  const request = ++citiesRequest
  cities.value = []
  if (!countryCode || !stateCode) return
  loadingCities.value = true
  try {
    const result = await SubscriptionService.getBillingCities(countryCode, stateCode)
    if (request === citiesRequest) cities.value = result || []
  } catch (e) {
    // Without a city list the field falls back to free text.
  } finally {
    if (request === citiesRequest) loadingCities.value = false
  }
}

watch(() => address.value?.country, (code) => loadStates(code))
watch(() => [address.value?.country, address.value?.state], ([countryCode, stateCode]) => loadCities(countryCode, stateCode))

onMounted(async () => {
  try {
    const [countryList, saved] = await Promise.all([
      SubscriptionService.getBillingCountries(),
      SubscriptionService.getBillingAddress().catch(() => null),
    ])
    countries.value = countryList || []
    const isKnown = (code) => countries.value.some(c => c.code === code)

    if (!address.value?.country) {
      const savedAddress = saved?.billingAddress
      if (savedAddress && isKnown(savedAddress.country)) {
        address.value = { street: '', city: '', state: '', zip: '', ...savedAddress }
      } else {
        const suggested = isKnown(saved?.suggestedCountry) ? saved.suggestedCountry : DEFAULT_COUNTRY
        address.value = { ...address.value, country: suggested }
      }
    } else {
      loadStates(address.value.country)
      loadCities(address.value.country, address.value.state)
    }
  } catch (e) {
    notify({ group: 'error', text: 'Unable to load countries. Please refresh the page.' })
  } finally {
    loadingCountries.value = false
  }
})

const errorClass = (field) => props.showErrors && missing.value[field]
  ? 'rounded ring-1 ring-error-light'
  : ''
</script>

<template>
  <div class="rounded-xl bg-surface-1 p-4 mb-4">
    <h3 class="text-headline font-medium text-surface-9 mb-2">Billing info</h3>
    <div class="flex flex-col gap-2">
      <FormSelect id="billing-country" v-model="country" :options="countryOptions" :disabled="loadingCountries"
        :class="errorClass('country')" />
      <FormInput id="billing-street" v-model="street" placeholder="Street Address" :maxlength="200"
        :is-error="showErrors && missing.street" />
      <div class="grid grid-cols-2 gap-2">
        <FormSelect v-if="showCitySelect" id="billing-city" v-model="city" :options="cityOptions"
          :disabled="!state || loadingCities" :class="errorClass('city')" />
        <FormInput v-else id="billing-city" v-model="city" placeholder="City" :maxlength="100"
          :is-error="showErrors && missing.city" />
        <FormInput id="billing-zip" v-model="zip" placeholder="ZIP" :maxlength="20"
          :is-error="showErrors && missing.zip" />
      </div>
      <FormSelect v-if="hasStates" id="billing-state" v-model="state" :options="stateOptions"
        :disabled="loadingStates" :class="errorClass('state')" />
    </div>
    <p v-if="showErrors && !isComplete" class="text-tiny text-error mt-2 mb-0">
      Please complete your billing address to continue.
    </p>
  </div>
</template>
